import React, { useEffect, useState } from "react"
import { ActivityIndicator, Image, Modal, FlatList } from 'react-native';
import type { BEEventProps, BuySquaresResponseProps, EventProps, LeagueProps, OfferResponseProps, PlayerBalanceProps, PlayerSquareProps, PublicPlayerProps, SquareOfferProps, SquarePrizeProps, SquareProps, SquareResultProps, SquaresCompetitionProps, SquaresPayoutProps, SquaresTypeProps } from "../types"
import Colors from "../constants/colors";
import { SqauresHelpers, SquaresApi } from "./api";
import { Icons, Toggle } from "../Components";
import SquaresBoard from "./components/SquaresBoard";
import BidForm from "./components/BidForm";
import SocketManager from "../Socket";
import EventCard from "./components/EventCard";
import ResultsCard from "./components/ResultsCard";
import SquaresDetails from "./components/SquaresDetails";
import BoardStats from "./components/BoardStats";
import SquareOwnersCard from "./components/SquareOwners";
import OfferForm from "./components/OfferForm";
import MySquaresCard from "./components/MySquaresCard";
import MyStatsCard from "./components/MyStatsCard";
import SquareOffersCard from "./components/SquareOffersCard";
import PrizeCard from './components/PrizeCard';
import { Button, Text, View } from "../Components/Themed";
import { useColors } from "../constants/useColors";

type SquaresModuleProps = {
    player_id?:string,
    distinct_id:string,
    info_header?:boolean,
    onClose:() => void,
    view_mode:'desktop'|'mobile',
    sq_comp_id:string,
    onManageSquares?:(sc:SquaresCompetitionProps) => void,
    insets?: { top:number, bottom:number },
    onRequestAuthenticate: (auth_strategy_id?:string) => void,
    onShareCompetition:(squares_competition:SquaresCompetitionProps) => void,
    onPlayerSelect:(player:PublicPlayerProps) => void,
    onEvent:(ev:BEEventProps) => void
}

const squares_sections = ['header', 'activate', 'offers', 'toggle', 'event', 'board', 'board_stats', 'my_stats', 'results', 'my_squares', 'winners','hidden_prizes','details', 'owners']

const SquaresModule = ({ sq_comp_id, info_header, view_mode, insets, player_id, distinct_id, onClose, onManageSquares, onRequestAuthenticate, onShareCompetition, onPlayerSelect }:SquaresModuleProps) => {
    const C = useColors();
    const [ active_tab, setActiveTab ] = useState('board');
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ module_size, setModuleSize ] = useState({ width:0, height:0 })
    const [ bid_form_expanded, setBidFormExpanded ] = useState(false);
    const [ updated_from_bid, setUpdatedFromBid ]= useState<BuySquaresResponseProps>();
    const [ updated_from_offer, setUpdatedFromOffer ]= useState<OfferResponseProps>();
    const [ my_data, setMyData ] = useState<{
        player_balance?:PlayerBalanceProps
    }>({

    })
    const { player_balance } = my_data;
    const [ module_data, setModuleData ] = useState<{
        loading:boolean,
        loaded:boolean,
        event?:EventProps,
        squares_competition?:SquaresCompetitionProps,
        squares:SquareProps[],
        square_prizes:SquarePrizeProps[],
        league?:LeagueProps,
        players:PublicPlayerProps[],
        squares_payout_type?:SquaresPayoutProps,
        squares_type?: SquaresTypeProps,
        square_results:SquareResultProps[],
        square_offers:SquareOfferProps[],
        player_square_history:PlayerSquareProps[],
        player_squares:PlayerSquareProps[],
    }>({
        loading: false,
        loaded: false,
        squares:[],
        players:[],
        square_results: [],
        square_prizes:[],
        square_offers: [],
        player_square_history: [],
        player_squares: []
    });
    const [ board_data, setBoardData ] = useState<{
        draft_square_offers:SquareOfferProps[],
        square_bids:PlayerSquareProps[],
        submit_ready: { is_ready: boolean, is_loading:boolean }
    }>({
        draft_square_offers: [],
        square_bids: [],
        submit_ready: { is_loading:false, is_ready: false }
    });

    const { square_bids, draft_square_offers, submit_ready } = board_data;
    const { loading, league, event, players, loaded, squares_competition, square_prizes, squares, squares_type, squares_payout_type, square_results, square_offers, player_square_history, player_squares } = module_data;
    const squares_expected_value = SqauresHelpers.getMyExpectedValue(squares, [ ...player_squares.filter(ps => ps.status == 'active' && ps.player_id == player_id) ], squares_competition, player_id)
    const my_squares = player_squares.filter(ps => ps.status == 'active' && ps.player_id == player_id);
    const home_abbr = event?.home?.abbr??squares_competition?.event_backup?.home?.abbr
    const away_abbr = event?.away?.abbr??squares_competition?.event_backup?.away?.abbr
    

    const dedupeWithMaxPrice = (arr: SquareOfferProps[]): SquareOfferProps[] => {
        const map = arr.reduce((acc, item) => {
            const existing = acc.get(item.sq_player_square_id);
            if (!existing || item.amount > existing.amount) {
                acc.set(item.sq_offer_id, item);
            }
            return acc;
        }, new Map<string, SquareOfferProps>());

        return Array.from(map.values());
    }

    const filterRelevantOffers = (offers:SquareOfferProps[]) => {
        let sell_offers = offers.filter(o => o.buy_sell_ind == 'sell')
        if(!player_id){ return sell_offers }
        //Now - only do buy offers that I am the offeree for
        let my_buy_offers = offers.filter(o => o.buy_sell_ind == 'buy' && o.offerer_id == player_id)
        let offered_to_me_buys = offers.filter(o => o.buy_sell_ind == 'buy' && o.offeree_id == player_id);
        offered_to_me_buys = dedupeWithMaxPrice(offered_to_me_buys)
        //We need to only get the max of the buys!

        return sell_offers.concat(my_buy_offers).concat(offered_to_me_buys)
    }

    let relevant_offers = filterRelevantOffers(square_offers)

    let toggle_options = [
        { key: 'info', label: 'Info', icon: <Icons.MenuIcon size={18} color={active_tab=='info'?C.text.white:C.text.action}/> },
        { key: 'board', label: 'Board', icon: <Icons.SquaresIcon size={18} color={active_tab=='board'?C.text.white:C.text.action}/> },
        { key: 'stats', label: 'Stats', icon: <Icons.PieIcon size={18} color={active_tab=='stats'?C.text.white:C.text.action}/> },
    ]

    if(my_squares.length > 0){
        toggle_options.push({ 
            key: 'my_squares', 
            label: 'Mine', 
            icon: (
                <View float style={{ backgroundColor:C.text.action, height:24, width:24, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                    <Text theme="h2" size={10} color={C.text.white}>{my_squares.length}</Text>
                </View>
            ) 
        })
    }

    if(square_results.length > 0){
        toggle_options.push({ key: 'results', label: 'Results', icon: <Icons.TrophyIcon size={18} color={active_tab=='results'?C.text.white:C.text.action}/> })
    }
    
    if(square_prizes.length > 0){
        toggle_options.push({ key: 'prizes', label: 'Prizes', icon: <Icons.AwardRibbonIcon size={18} color={active_tab=='prizes'?C.text.white:C.text.action}/> })
    }


    useEffect(() => {
        if(!sq_comp_id){ return }
        if(!loaded){ SquaresApi.setEnvironment(); }
        getDataFromServer(sq_comp_id);
    },[sq_comp_id, player_id])

    useEffect(() => {
        if(!updated_from_bid?.squares_competition){ return }
        handleUpdateBoardFromBuy(updated_from_bid)
    },[updated_from_bid])

    useEffect(() => {
        if(!updated_from_offer?.squares_competition){ return }
        handleUpdateBoardFromOffer(updated_from_offer)
    },[updated_from_offer])

    const getDataFromServer = async(sq_comp_id:string, options?:any) => {
        if(!options || !options.ignore_load){ setModuleData({ ...module_data, loading: true }) }
        const d = await SquaresApi.getSquareCompetitionById(sq_comp_id);
        const evs = await SquaresApi.getEventsByEventIds([d.squares_competition.event_id]);
        const league_id = evs[0]?.league_id
        let unique_player_ids = [ ...new Set(d.square_offers.map(so => so.offerer_id).concat(d.player_squares.map(ps => ps.player_id))) ]
        if(player_id){ 
            unique_player_ids.push(player_id) 
            const pb = await SquaresApi.getMyBalance();
            setMyData({
                ...my_data,
                player_balance:pb
            })
        }
        let ps = await SquaresApi.getPlayersByPlayerIds(unique_player_ids)
        let l:LeagueProps | undefined = undefined
        if(league_id){
            l = await SquaresApi.getLeagueById(league_id);
        }
        setModuleData({
            ...module_data,
            loaded: true,
            event: evs[0],
            league: l,
            squares_competition: d.squares_competition,
            squares_type: d.squares_type,
            squares_payout_type: d.squares_payout_type,
            squares:d.squares,
            players:ps,
            square_prizes: d.square_prizes,
            square_offers: d.square_offers,
            square_results: d.square_results,
            player_squares:d.player_squares.filter(ps => ps.status == 'active'),
            player_square_history: d.player_square_history
        })
    }


    const handleSubmitBid = async(promo_balance?:boolean) => {
        if(!squares_competition){ return }
        if(!player_id){ 
            setBoardData({ ...board_data, square_bids:[], submit_ready:{ is_loading: false, is_ready: false } })
            setTimeout(() => onRequestAuthenticate(squares_competition.auth_strategy_id), 500);
            return  
        }
        if(!submit_ready.is_ready){
            setBoardData({ ...board_data, submit_ready: { is_ready: false, is_loading:true } })
            setTimeout(() => {
                setBoardData({ ...board_data, submit_ready: { is_ready: true, is_loading:false } })
            }, 500);
            return
        }
        //if(player?.vouched_status !== 'verified' && squares_competition.market_type === 'FOR_MONEY'){ return alert('Must be verified before purchasing real money squares') }
        if(submit_ready.is_loading){ return } //Prevent button mashing
        //SUBMIT BIDS!
        setBoardData({ ...board_data, submit_ready: { ...submit_ready, is_loading:true } })
        await SquaresApi.buySquares(squares_competition.sq_comp_id, square_bids, promo_balance)
        //if(needs_reload){ handleUpdateBoardFromBuy(resp) } //If we somehow got disconnected from the socket, we need to load it
        setBoardData({ ...board_data, square_bids:[], submit_ready:{ is_loading: false, is_ready: false } })
        setBidFormExpanded(false);
    }

    const handleSubmitOffer = async() => {
        if(draft_square_offers.length === 0 || !draft_square_offers[0]){ return }
        if(!submit_ready.is_ready){
            setBoardData({ ...board_data, submit_ready: { is_ready: false, is_loading:true } })
            setTimeout(() => {
                setBoardData({ ...board_data, submit_ready: { is_ready: true, is_loading:false } })
            }, 500);
            return
        }
        if(submit_ready.is_loading){ return } //Prevent button mashing
        //SUBMIT BIDS!
        
        setBoardData({ ...board_data, submit_ready: { ...submit_ready, is_loading:true } })
        await SquaresApi.offserSquare(draft_square_offers[0])
        //if(needs_reload){ handleUpdateBoardFromOffer(resp) } //If we somehow got disconnected from the socket, we need to load it
        setBoardData({ ...board_data, draft_square_offers:[], submit_ready:{ is_loading: false, is_ready: false } })
    }

    const handleClearBids = () => {
        setBoardData({ ...board_data, square_bids: [], submit_ready: { is_ready:false, is_loading:false }})
        setBidFormExpanded(false);
    }

    const handleClearOffers = () => {
        setBoardData({ ...board_data, draft_square_offers: [], submit_ready: { is_ready:false, is_loading:false }})
    }

    const handleSquareBid = (square:SquareProps, action:'remove'|'add') => {
        if(!squares_competition){ return }
        //setSubmitReady({ isReady:false, isLoading:false })
        if(action === 'remove'){
            return setBoardData({ ...board_data, square_bids: square_bids.filter(b => b.sq_square_id != square.sq_square_id) })
        }
        let purchase_price = square.init_cost
        if(square.last_purchase_price >= square.init_cost){ purchase_price = square.last_purchase_price + 0.50 }
        let newPS:PlayerSquareProps = {
            sq_player_square_id:'', sq_comp_id:squares_competition.sq_comp_id, sq_square_id:square.sq_square_id, player_id:'', purchase_price, status:'active', create_datetime:'', last_update_datetime:''
        }
        return setBoardData({ ...board_data, square_bids: square_bids.concat(newPS) })
    }

    const handleBidAmountChange = (square_bid:PlayerSquareProps, square:SquareProps, direction:string) => {
        let new_purchase_price:number = 0
        if(direction === 'increase'){
            new_purchase_price = square_bid.purchase_price + 0.50
        } else {
            new_purchase_price = square_bid.purchase_price - 0.50
            if(new_purchase_price < square.init_cost){ return } //Do not change if the price is less than the initial cost
            if(new_purchase_price <= square.last_purchase_price){ return } //Do not change if the price is not greater than current bid
        }
        
        square_bid = { ...square_bid, purchase_price:new_purchase_price }
        setBoardData({ ...board_data, square_bids: square_bids.filter(s => s.sq_square_id != square_bid.sq_square_id).concat(square_bid) })
    }

    const handleOfferAmountChange = (square_offer:SquareOfferProps, player_square:PlayerSquareProps, direction:string) => {
        if(!player_square){ return }
        let new_purchase_price:number = 0
        if(direction === 'increase'){
            new_purchase_price = square_offer.amount + 1
        } else {
            new_purchase_price = square_offer.amount - 1
        }
        if(new_purchase_price <= 0){ return } //Do not change if the price is less than the initial cost
        square_offer = { ...square_offer, amount:new_purchase_price }
        setBoardData({ ...board_data, draft_square_offers: draft_square_offers.filter(o => o.sq_player_square_id != square_offer.sq_player_square_id).concat(square_offer) })
    }

    const handleSquareOffer = (square:SquareProps, action:'remove'|'add') => {
        if(!squares_competition){ return }
        if(!player_id){ return }
        //setSubmitReady({ isReady:false, isLoading:false })
        let ps = player_squares.find(ps => ps.sq_square_id == square.sq_square_id)
        if(!ps){ return alert('Cannot put offer on this square as it is not owned by anyone') }        
        if(action === 'remove'){
            return setBoardData({ ...board_data, draft_square_offers: draft_square_offers.filter(so => so.sq_player_square_id != ps?.sq_player_square_id) })
        }
        if(draft_square_offers.length > 0){ return alert('Currently only allowed to offer to buy 1 square at a time') }
        //Todo - check if there is an existing square offer and update more than the last one (maybe)
        let purchase_price = ps.purchase_price + 1
        let newSO:SquareOfferProps = {
            sq_offer_id:'', sq_comp_id:squares_competition.sq_comp_id, sq_player_square_id:ps.sq_player_square_id, amount:purchase_price, buy_sell_ind:ps.player_id == player_id?'sell':'buy', market_type:squares_competition.market_type,
            status:'offered', offerer_id: player_id, offeree_id:ps.player_id, create_datetime:'', last_update_datetime:''
        }
        if(newSO.buy_sell_ind == 'buy'){
            let existingSellOffer = square_offers.find(o => o.sq_player_square_id == newSO.sq_player_square_id)
            if(existingSellOffer){ newSO.amount = existingSellOffer.amount }
        }
        if(newSO.buy_sell_ind == 'sell'){
            let existingSellDraftOffer = draft_square_offers.find(o => o.sq_player_square_id == newSO.sq_player_square_id)
            if(existingSellDraftOffer){ return alert('You already have an existing sale offer for this square') }
        }
        return setBoardData({ ...board_data, draft_square_offers: draft_square_offers.concat(newSO) })
    }

    const handleUpdateBoardFromOffer = (data:OfferResponseProps) => {
        //First remove squares
        //let new_ps = player_squares.filter(ps => !data.removed_player_squares.find(nps => nps.sq_player_square_id == ps.sq_player_square_id))
        //Now update squares
        let new_ps = SqauresHelpers.updatePlayerSquaresWithSavedSquares([ ...player_squares ], data.saved_player_squares)
        new_ps = SqauresHelpers.updatePlayerSquaresWithRemovedSquares(new_ps, data.removed_player_squares);
        setModuleData({
            ...module_data,
            squares_competition: data.squares_competition ? data.squares_competition : squares_competition,
            player_squares: new_ps,
            square_offers: SqauresHelpers.updateSquareOffersWithUpdatedOffers(square_offers, data.updated_offers)
        })
    }

    const handleActivate = async() => {
        if(!squares_competition || loading){ return }
        if(squares_competition.status != 'pending'){ return }
        setModuleData({ ...module_data, loading: true });
        const activate_resp = await SquaresApi.activateSquares(squares_competition.sq_comp_id);
        if(!activate_resp){ return alert('Unable to activate competition') }
        setModuleData({ 
            ...module_data, 
            squares_competition: activate_resp.squares_competition, 
            player_squares: activate_resp.player_squares,
            squares: activate_resp.squares,
            squares_type: activate_resp.squares_type,
            squares_payout_type: activate_resp.squares_payout_type,
            loading: true 
        });
    }

    const handleUpdateBoardFromBuy = (data:BuySquaresResponseProps) => {
        //First remove squares
        //Now update squares
        let new_ps = SqauresHelpers.updatePlayerSquaresWithSavedSquares([ ...player_squares ], data.saved_player_squares)
        new_ps = SqauresHelpers.updatePlayerSquaresWithRemovedSquares(new_ps, data.removed_player_squares);
        let new_sq = SqauresHelpers.updateSquaresWithUpdatedSquares([ ...squares], data.updated_squares)
        let new_ps_his = SqauresHelpers.updatePlayerSquareHistoryWithRemovedSquares([ ...player_square_history], data.removed_player_squares);
        setModuleData({
            ...module_data,
            squares_competition: data.squares_competition,
            squares: new_sq,
            player_squares: new_ps,
            player_square_history: new_ps_his
        })
    }

    const renderSections = (data: { item: string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!squares_competition){ return <></> }
                if(info_header && active_tab != 'info'){ return <></> }
                return (
                    <View type='header' nativeID="squares_header" style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                        <View transparent>
                            <Image
                                source={{ uri: squares_competition.image?.url ?? 'https://res.cloudinary.com/hoabts6mc/image/upload/v1649737862/be_logo_jte2ux.webp' }}
                                style={{ height:50, width:50, borderRadius:4 }}
                                resizeMode="cover"
                            />
                        </View>
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>{squares_competition.sq_comp_name}</Text>
                            <Text style={{ marginTop:4 }} theme='h2'>{squares_competition.sq_comp_description}</Text>
                        </View>
                        {onManageSquares && player_id == squares_competition?.admin_id ?
                        <Button float style={{ padding:10, marginRight:10 }} onPress={() => onManageSquares(squares_competition)}>
                            <Icons.SettingsIcon size={18} color={C.text.h1} />
                        </Button>
                        :<></>}
                        <Button float onPress={() => onShareCompetition(squares_competition)}>
                            <Icons.ShareIcon color={C.text.h1} size={14} />
                        </Button>
                    </View>
                )
            case 'activate':
                if(squares_competition?.status != 'pending'){ return <></> }
                if(player_id != squares_competition?.admin_id){ return <></> }
                return (
                    <View type='row' style={{ padding:10 }}>
                        <View style={{ flex:1 }}>
                            <Text theme="h2">Squares competition is pending.</Text>
                            <Text theme="description" style={{ marginTop:3 }}>Press Activate to allow others to join!</Text>
                        </View>
                        <Button
                            disabled={loading ? true : false}
                            style={{ marginLeft:5, padding:10 }}
                            loading={loading}
                            title="ACTIVATE!"
                            type='success'
                            onPress={() => handleActivate()}
                        />
                    </View>
                )
            case 'event':
                if(!event){ return <></> }
                return (
                    <EventCard
                        hidden={active_tab == 'info' ? false : true}
                        disable_collapse
                        event={event}
                        league={league}
                    />
                )
            case 'offers':
                if(relevant_offers.length == 0 || !squares_competition){ return <></> }
                return (
                    <View>
                        <SquareOffersCard
                            player_squares={player_squares}
                            square_offers={relevant_offers}
                            player_id={player_id}
                            squares={squares}
                            players={players}
                            home_abbr={home_abbr}
                            away_abbr={away_abbr}
                            onRequestAuthenticate={() => onRequestAuthenticate(squares_competition.auth_strategy_id)}
                        />
                    </View>
                )
            case 'toggle':
                return (
                    <View style={{ padding:8 }}>
                        <Toggle
                            options={toggle_options}
                            active_toggle_color={C.text.action}
                            selected_option={active_tab}
                            toggle_padding={5}
                            onSelectOption={(key) => setActiveTab(key)}
                            title_renderer={(option) => {
                                return (
                                    <View transparent style={{ flexGrow:1, alignItems:'center' }}>
                                        <View transparent style={{ flexGrow:1 }}>
                                        {option.icon}
                                        </View>
                                        <Text style={{ marginTop:3 }} size={10} theme="light" textAlign="center" color={active_tab == option.key ? C.text.white : C.text.action}>{option.label}</Text>
                                    </View>
                                )
                            }}
                        />
                    </View>
                )
            case 'board_stats':
                if(!squares_competition){ return <></> }
                return (
                        <BoardStats
                            hidden={active_tab == 'stats' ? false : true}
                            disable_collapse
                            squares_competition={squares_competition}
                            squares={squares}
                            player_squares={player_squares}
                        />

                )
            case 'my_stats':
                if(!squares_competition){ return <></> }
                return (
                    <MyStatsCard
                        hidden={active_tab == 'my_squares' ? false : true}
                        player_id={player_id}
                        players={players}
                        home_abbr={home_abbr}
                        squares_expected_value={squares_expected_value}
                        away_abbr={away_abbr}
                        onSquareBid={(square, action) => {
                            handleSquareBid(square, action)
                            setBidFormExpanded(true);
                        }}
                        squares={squares}
                        player_square_history={player_square_history}
                        player_squares={player_squares}
                        square_results={square_results}
                        squares_competition={squares_competition}
                    />
                )
            case 'results':
                if(square_results.length == 0){ return <></> }
                return (
                    <ResultsCard
                        hidden={active_tab == 'results' ? false : true}
                        disable_collapse
                        home_abbr={home_abbr}
                        away_abbr={away_abbr}
                        squares={squares}
                        square_results={square_results}
                        onPlayerSelect={onPlayerSelect}
                    />
                )
            case 'hidden_prizes':
                return (
                    <View style={{ padding:10 }}>

                    <PrizeCard 
                        square_prizes={square_prizes}
                        squares={squares}
                        hidden={active_tab == 'prizes' ? false : true}
                        disable_collapse
                        players={players}
                        event={event}
                    />
                    </View>
                )
            case 'details':
                if(!squares_competition){ return <></> }
                return (
                    <View transparent>
                        <SquaresDetails
                            squares_competition={squares_competition}
                            hidden={active_tab == 'info' ? false : true}
                            disable_collapse
                            squares_payout_type={squares_payout_type}
                            squares_type={squares_type}
                        />
                    </View>
                    
                    
                )
            case 'owners':
                return (
                    <SquareOwnersCard
                        players={players}
                        player_squares={player_squares}
                        hidden={active_tab == 'board' ? false : true}
                        onPlayerSelect={onPlayerSelect}
                    />
                )
            case 'board':
                if(!squares_competition){ return <></> }
                if(squares_competition.status == 'pending'){ return <></> }
                return (
                    <View style={{ alignItems:view_mode == 'desktop' ? 'center': undefined }}>
                        <SquaresBoard 
                            player_id={player_id}
                            hidden={active_tab == 'board' ? false : true}
                            disable_collapse
                            onSquareBid={handleSquareBid}
                            onSquareOffer={handleSquareOffer}
                            onRefreshCompetition={() => getDataFromServer(sq_comp_id)}
                            square_bids={square_bids}
                            draft_square_offers={draft_square_offers}
                            squares_competition={squares_competition}
                            squares={squares}
                            event={event}
                            player_squares={player_squares}
                            player_square_history={player_square_history}
                            square_results={square_results}
                            square_offers={square_offers}
                        />
                    </View>
                )
            case 'my_squares':
                if(my_squares.length == 0){ return <></> }
                return (
                    <View style={{ padding:10 }}>

                    <MySquaresCard
                        player_squares={my_squares}
                        hidden={active_tab == 'my_squares' ? false : true}
                        disable_collapse
                        squares={squares}
                        square_offers={square_offers}
                        away_abbr={away_abbr}
                        my_square_values={squares_expected_value.my_square_values}
                        home_abbr={home_abbr}
                        comp_status={comp_status}
                        onSquareOffer={handleSquareOffer}
                    />
                    </View>
                )
            default: return <></>
        }
    }

    if(loading || !squares_competition){
        return (
            <View style={{ flex:1 }}>
                <ActivityIndicator style={{ padding:20, alignSelf:'center' }} color={C.text.h1} size='large' />
            </View>
        )
    }

    const comp_status = SqauresHelpers.getCompStatus(event, squares_competition)
    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { width, height } = ev.nativeEvent.layout
            setModuleSize({ width, height })
        }}> 
            <View transparent style={{ flex:1 }}>
                <FlatList
                    key='squares_section'
                    data={squares_sections}
                    renderItem={renderSections}
                    keyExtractor={(item) => item}
                /> 
                {squares_competition?.status == 'archived' ?
                <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                    <View float style={{ width: '70%' }}>
                        <View type='header' style={{ justifyContent:'center', alignItems:'center', padding:10 }}>
                            <Text theme='h1'>Competition Has Been Deleted</Text>
                        </View>
                        <View style={{ padding:10, justifyContent:'center', alignItems:'center' }}>
                            <Image
                                source={{ uri: squares_competition.image?.url }}
                                style={{ height:75, width: 75 }}
                                resizeMode='cover'
                            />
                            <Text theme='h1'>{squares_competition.sq_comp_name}</Text>
                        </View>
                        <View type='footer' style={{ padding:10 }}>
                            <Button
                                title='CLOSE'
                                type='close'
                                onPress={() => onClose()}
                            />
                        </View>
                    </View>
                </View>
                :<></>}
            </View>           
           
            <SocketManager 
                //access_token={access_token}
                onConnect={() => {
                    if(needs_reload){
                        setNeedsReload(false);
                        getDataFromServer(sq_comp_id, { ignore_load:true });
                    }
                }}
                onDisconnect={() => setNeedsReload(true)}
                distinct_id={distinct_id}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'V1_UPDATE_SQUARE_DETAILS':
                            let data = ev.data as BuySquaresResponseProps | OfferResponseProps
                            if(!data.squares_competition || data.squares_competition.sq_comp_id != squares_competition.sq_comp_id){ break } //This isn't this competition;
                            if(ev.action == 'buy'){ setUpdatedFromBid(data as BuySquaresResponseProps) }
                            if(ev.action == 'offer')[ setUpdatedFromOffer(data as OfferResponseProps) ]
                            break;
                        case 'ACTIVATE_SQUARES':
                            //When activated - we should just reload the modules
                            let socket_comp = ev.squares_competition as SquaresCompetitionProps
                            if(!socket_comp){return}
                            if(socket_comp.sq_comp_id != sq_comp_id){ return } //Dont do anything here
                            getDataFromServer(sq_comp_id, {});
                            break;
                        case 'DELETE_SQUARES':
                            //When activated - we should just reload the modules
                            let delete_comp = ev.squares_competition as SquaresCompetitionProps
                            if(!delete_comp){return}
                            if(delete_comp.sq_comp_id != sq_comp_id){ return } //Dont do anything here
                            getDataFromServer(sq_comp_id, {});
                            break;
                        default: break;
                    }
                }}
                subscribed_events={['V1_UPDATE_SQUARE_DETAILS', 'ACTIVATE_SQUARES', 'DELETE_SQUARES']}
            />
            <Modal
                transparent
                visible={bid_form_expanded}
                animationType={view_mode=='desktop' ? 'fade' : 'slide'}
            >
            <View type='blur' style={view_mode=='desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1, justifyContent:'flex-end', paddingBottom:insets?.bottom, padding:10 }}>
                <View transparent style={view_mode=='desktop'?{ width:500 }:undefined}>
                <BidForm
                    player_id={player_id}
                    player_balance={player_balance}
                    home_abbr={home_abbr}
                    away_abbr={away_abbr}
                    width={module_size.width}
                    squares={squares}
                    submit_ready={submit_ready}
                    onBidAmountChange={(pb, sq, dir) => handleBidAmountChange(pb, sq, dir)}
                    market_type={squares_competition.market_type}
                    square_bids={square_bids}
                    onClearBids={handleClearBids}
                    onRequestAuthenticate={() => {
                        setBidFormExpanded(false);
                        setTimeout(() => {
                            onRequestAuthenticate(squares_competition.auth_strategy_id)
                        }, 500);
                    }}
                    onSubmitBid={(promo_balance) => handleSubmitBid(promo_balance)}
                />
                </View>
                </View>
            </Modal>
            <Modal
                transparent
                visible={draft_square_offers?.length > 0?true:false}
                animationType={view_mode=='desktop' ? 'fade' : 'slide'}
            >
                <View type='blur' style={view_mode=='desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1, justifyContent:'flex-end', paddingBottom:insets?.bottom, padding:10 }}>
                    <View transparent style={view_mode=='desktop'?{ width:500 }:undefined}>
                        <OfferForm
                            player_id={player_id}
                            draft_square_offers={draft_square_offers}
                            home_abbr={home_abbr}
                            player_balance={player_balance}
                            width={module_size.width}
                            away_abbr={away_abbr}
                            squares={squares}
                            submit_ready={submit_ready}
                            market_type={squares_competition.market_type}
                            player_squares={player_squares}
                            onClearOffers={handleClearOffers}
                            onRequestAuthenticate={() => {
                                setBoardData({ ...board_data, draft_square_offers:[], submit_ready:{ is_loading: false, is_ready: false } })
                                setTimeout(() => onRequestAuthenticate(squares_competition.auth_strategy_id), 500);
                            }}
                            onOfferAmountChange={(offer, player_square, direction) => handleOfferAmountChange(offer, player_square, direction)}
                            onSubmitOffer={() => handleSubmitOffer()}
                        />
                    </View>
                </View>
            </Modal>
             {square_bids.length > 0 && !bid_form_expanded ?
                <View float style={{ position:'absolute', bottom:insets?.bottom ?? 0 + 10, left:10, padding:10}}>
                    <View transparent style={{ flexDirection:'row', alignItems:'center'}}>
                        <Text theme="h1">{square_bids.length} Squares</Text>
                        <Button
                            title="SET AMOUNTS"
                            style={{ marginLeft:10}}
                            title_color={Colors.shades.white}
                            backgroundColor={Colors.brand.electric}
                            onPress={() => setBidFormExpanded(true)}
                        />
                    </View>
                </View>
            :<></>}
        </View>

    )
}

export default SquaresModule